import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { removeStorageFile } from '@/lib/storage';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

// Forma canónica de producto que consumen las páginas (coincide con el mock).
export interface Product {
  id: string;
  sku: string;
  name: string;
  slug: string;
  categoryId: string;
  description: string;
  features: string[];
  materials: string;
  story: string;
  benefits: string[];
  commercialPrice: number;
  unitCost: number;
  images: string[];
  videos: string[];
  active: boolean;
  isPresaleAllowed: boolean;
  minStockAlert: number;
  // Línea comercial: 'premium' (envío incluido) o 'sencilla' (cobra flete).
  lineType: string;
  // Si es true, el checkout/venta directa suma el flete de Interrapidísimo.
  chargeShipping: boolean;
}

// Entrada del formulario de producto (crear/editar).
export interface ProductInput {
  id?: string;
  name: string;
  sku: string;
  categoryId: string;
  description: string;
  features: string[];
  materials: string;
  story: string;
  benefits: string[];
  commercialPrice: number;
  unitCost: number;
  images: string[];
  videos: string[];
  active: boolean;
  lineType: string;
  chargeShipping: boolean;
}

// Categoría maestra (tabla `categories`).
export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
}

// Fila real de la tabla `products` en Supabase.
interface DbProduct {
  id: string;
  sku_prefix: string;
  name: string;
  slug: string | null;
  category: string;
  description: string | null;
  features: string[] | null;
  materials: string[] | null;
  story: string | null;
  benefits: string[] | null;
  images: string[] | null;
  videos: string[] | null;
  unit_cost: number | string;
  retail_price: number | string;
  is_active: boolean | null;
  line_type: string | null;
  charge_shipping: boolean | null;
}

// Fila real de la tabla `categories`.
interface DbCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}

export interface ProductsResult {
  data: Product[];
}

export interface CategoriesResult {
  data: Category[];
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Convierte enlaces de compartir de Google Drive en imágenes directas para que
// se puedan incrustar. Google Photos e Instagram no permiten hotlink, así que
// esos enlaces se dejan tal cual (SafeImage mostrará un enlace "Ver foto").
export function normalizeImageUrl(raw: string): string {
  const url = raw.trim();
  const id = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/i);
  if (id) return `https://drive.google.com/uc?export=view&id=${id[1]}`;
  return url;
}

// Empareja el texto de categoría de un producto (p. ej. "Perros Grandes")
// contra la categoría maestra real. Prueba slug, nombre exacto y, finalmente,
// coincidencia por subcadena para tolerar desfases de datos.
function matchCategoryId(categoryText: string, categories: Category[]): string {
  const text = categoryText.trim().toLowerCase();
  const slug = slugify(categoryText);

  let match = categories.find((c) => c.slug === slug);
  if (match) return match.id;

  match = categories.find((c) => c.name.toLowerCase() === text);
  if (match) return match.id;

  match = categories.find(
    (c) => text.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(text),
  );
  if (match) return match.id;

  return slug;
}

// Mapea una fila de la tabla `products` a la forma canónica del proyecto.
function mapDbProduct(row: DbProduct, categories: Category[]): Product {
  return {
    id: row.id,
    sku: row.sku_prefix,
    name: row.name,
    slug: row.slug ?? slugify(row.name),
    categoryId: matchCategoryId(row.category, categories),
    description: row.description ?? '',
    features: row.features ?? [],
    materials: (row.materials ?? []).join(', '),
    story: row.story ?? '',
    benefits: row.benefits ?? [],
    commercialPrice: Number(row.retail_price),
    unitCost: Number(row.unit_cost),
    images: row.images ?? [],
    videos: row.videos ?? [],
    active: row.is_active ?? true,
    isPresaleAllowed: false,
    minStockAlert: 0,
    lineType: row.line_type ?? 'premium',
    chargeShipping: row.charge_shipping ?? false,
  };
}

function mapDbCategory(row: DbCategory): Category {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description ?? '',
  };
}

// Consulta las categorías maestras reales con fallback automático al mock.
export async function fetchCategories(): Promise<CategoriesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('categories').select('*');

    if (error) throw new Error(error.message);

    return { data: ((data ?? []) as DbCategory[]).map(mapDbCategory) };
  } catch (err) {
    console.error('[products] categories connection failed:', err);
    throw err;
  }
}

// Consulta de productos usando las categorías maestras reales para el mapeo,
// con fallback automático al mock:
//   - sin credenciales -> mock
//   - error de conexión/consulta -> mock
//   - tabla vacía -> mock
export async function fetchProducts(): Promise<ProductsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [productsRes, categoriesRes] = await Promise.all([
      supabase.from('products').select('*'),
      supabase.from('categories').select('*'),
    ]);

    if (productsRes.error) throw new Error(productsRes.error.message);
    if (categoriesRes.error) throw new Error(categoriesRes.error.message);

    const rows = (productsRes.data ?? []) as DbProduct[];
    const categories = ((categoriesRes.data ?? []) as DbCategory[]).map(mapDbCategory);

    return {
      data: rows.map((row) => mapDbProduct(row, categories)),
    };
  } catch (err) {
    console.error('[products] Supabase connection failed:', err);
    throw err;
  }
}

// Convierte el nombre de categoría maestro en el texto que guarda la tabla
// `products` (p. ej. "Perros Grandes" -> "Perros"). La columna `category` de la
// tabla guarda un texto corto, no un id.
function categoryTextFor(categoryId: string, categories: Category[]): string {
  const match = categories.find((c) => c.id === categoryId);
  return match ? match.name : '';
}

// Guarda (crea o actualiza) un producto en Supabase. Devuelve el error en texto
// legible para mostrarlo en el formulario, o null si todo salió bien.
export async function saveProduct(
  input: ProductInput,
  categories: Category[],
): Promise<{ data: Product | null; error: string | null }> {
  if (!(await canWrite('products'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }

  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado. Conéctalo para guardar.' };
  }

  const categoryText = categoryTextFor(input.categoryId, categories);

  const payload = {
    name: input.name.trim(),
    sku_prefix: input.sku.trim(),
    category: categoryText,
    slug: slugify(input.name),
    description: input.description.trim() || null,
    features: input.features.filter((f) => f.trim().length > 0),
    materials: input.materials
      .split(',')
      .map((m) => m.trim())
      .filter((m) => m.length > 0),
    story: input.story.trim() || null,
    benefits: input.benefits.filter((b) => b.trim().length > 0),
    images: input.images.filter((i) => i.trim().length > 0).map(normalizeImageUrl),
    videos: input.videos.filter((v) => v.trim().length > 0),
    unit_cost: Number(input.unitCost) || 0,
    retail_price: Number(input.commercialPrice) || 0,
    is_active: input.active,
    line_type: input.lineType || 'premium',
    charge_shipping: input.chargeShipping,
  };

  try {
    let res;
    if (input.id) {
      res = await supabase.from('products').update(payload).eq('id', input.id).select('*');
    } else {
      res = await supabase.from('products').insert(payload).select('*');
    }

    if (res.error) {
      console.warn('[products] save failed:', res.error.message);
      return { data: null, error: res.error.message };
    }

    const row = res.data?.[0] as DbProduct | undefined;
    if (!row) {
      return { data: null, error: 'No se pudo leer el producto guardado.' };
    }

    // Trazabilidad: registra la operación en audit_logs (no bloqueante).
    recordAudit({
      entityType: 'products',
      entityId: row.id,
      action: input.id ? 'UPDATE' : 'CREATE',
      fieldName: input.id ? 'name' : null,
      oldValue: null,
      newValue: input.name,
    });

    return { data: mapDbProduct(row, categories), error: null };
  } catch (err) {
    console.warn('[products] save connection failed:', err);
    return { data: null, error: 'Error de conexión al guardar el producto.' };
  }
}

// Sube una imagen de producto al bucket público `product-images` y devuelve la
// URL pública lista para guardar en el array `images` del producto. El nombre
// se sanitiza (minúsculas, sin espacios ni caracteres raros) y se antepone un
// timestamp para evitar colisiones.
export async function uploadProductImage(
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  return uploadProductMedia(file, 'image');
}

// Sube un video al bucket público `product-images` y devuelve la URL pública
// lista para usar como <video src>. Acepta mp4, webm, mov y cualquier formato
// de video; el nombre se sanitiza igual que las imágenes.
export async function uploadProductVideo(
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  return uploadProductMedia(file, 'video');
}

// Lógica compartida de subida de archivos (imagen o video) al bucket público
// `product-images`. El bucket es público, así que la URL devuelta funciona
// tanto en <img> como en <video> sin pasos extra.
async function uploadProductMedia(
  file: File,
  fallbackName: string,
): Promise<{ url: string | null; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { url: null, error: 'Supabase no está configurado. Conéctalo para subir archivos.' };
  }

  const safeBase =
    file.name
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || fallbackName;

  const fallbackExt = file.type.startsWith('video/') ? 'mp4' : 'jpg';
  const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || fallbackExt;
  const path = `${Date.now()}-${safeBase}.${ext}`;

  try {
    const { error } = await supabase.storage
      .from('product-images')
      .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });

    if (error) return { url: null, error: error.message };

    const { data } = supabase.storage.from('product-images').getPublicUrl(path);
    return { url: data.publicUrl, error: null };
  } catch (err) {
    console.warn('[products] upload media failed:', err);
    return { url: null, error: 'Error de conexión al subir el archivo.' };
  }
}

// Borra una imagen de producto del bucket `product-images` a partir de su URL
// pública. Si la URL es externa (no de Storage propio), no hace nada.
export async function deleteProductImage(url: string): Promise<{ error: string | null }> {
  return removeStorageFile('product-images', url);
}

export interface CategoryInput {
  name: string;
  description: string;
}

// Crea una categoría nueva en Supabase.
export async function createCategory(
  input: CategoryInput,
): Promise<{ data: Category | null; error: string | null }> {
  if (!(await canWrite('products'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    name: input.name.trim(),
    slug: slugify(input.name),
    description: input.description.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('categories').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as DbCategory | undefined;
    if (!row) return { data: null, error: 'No se pudo crear la categoría.' };
    recordAudit({
      entityType: 'categories',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.name,
    });
    return { data: mapDbCategory(row), error: null };
  } catch (err) {
    console.warn('[categories] create failed:', err);
    return { data: null, error: 'Error de conexión al crear la categoría.' };
  }
}

// Actualiza una categoría existente.
export async function updateCategory(
  id: string,
  input: CategoryInput,
): Promise<{ data: Category | null; error: string | null }> {
  if (!(await canWrite('products'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    name: input.name.trim(),
    slug: slugify(input.name),
    description: input.description.trim() || null,
  };

  try {
    const { data, error } = await supabase
      .from('categories')
      .update(payload)
      .eq('id', id)
      .select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as DbCategory | undefined;
    if (!row) return { data: null, error: 'No se pudo actualizar la categoría.' };
    recordAudit({
      entityType: 'categories',
      entityId: id,
      action: 'UPDATE',
      fieldName: 'name',
      newValue: input.name,
    });
    return { data: mapDbCategory(row), error: null };
  } catch (err) {
    console.warn('[categories] update failed:', err);
    return { data: null, error: 'Error de conexión al actualizar la categoría.' };
  }
}

// Elimina una categoría. Si tiene productos asignados, se bloquea con un
// mensaje claro para no dejar referencias huérfanas en el catálogo.
export async function deleteCategory(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('products'))) {
    return { error: DELETE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { data: cat } = await supabase
      .from('categories')
      .select('name')
      .eq('id', id)
      .maybeSingle();

    if (cat?.name) {
      const { data: used } = await supabase
        .from('products')
        .select('id')
        .eq('category', cat.name)
        .limit(1);
      if (used && used.length > 0) {
        return {
          error:
            'No se puede eliminar: hay productos asignados a esta categoría. Reasigna esos productos primero.',
        };
      }
    }

    const { error } = await supabase.from('categories').delete().eq('id', id);
    if (error) return { error: error.message };

    recordAudit({
      entityType: 'categories',
      entityId: id,
      action: 'DELETE',
    });
    return { error: null };
  } catch (err) {
    console.warn('[categories] delete failed:', err);
    return { error: 'Error de conexión al eliminar la categoría.' };
  }
}

export interface StockEntry {
  sizeId: string;
  quantity: number;
}

// Sincroniza las variantes de un producto y su stock por talla. Para cada
// entrada: si la variante ya existe actualiza su stock; si no existe y la
// cantidad es mayor a 0, crea la variante + fila de inventario. Idempotente.
export async function syncProductStock(
  productId: string,
  skuPrefix: string,
  entries: StockEntry[],
): Promise<{ error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { data: existing } = await supabase
      .from('product_variants')
      .select('id, size_id')
      .eq('product_id', productId);

    const variantBySize = new Map<string, string>();
    for (const v of (existing ?? []) as Array<{ id: string; size_id: string }>) {
      variantBySize.set(v.size_id, v.id);
    }

    let seq = (existing ?? []).length;

    for (const entry of entries) {
      const variantId = variantBySize.get(entry.sizeId);

      if (variantId) {
        // Reactiva la variante (por si estaba desactivada). No se toca el stock
        // existente: las cantidades se gestionan desde Compras / Inventario.
        await supabase
          .from('product_variants')
          .update({ is_active: true })
          .eq('id', variantId);
        continue;
      }

      seq += 1;
      const sku = `${skuPrefix}-${seq}`;
      const { data: v, error: vErr } = await supabase
        .from('product_variants')
        .insert({
          product_id: productId,
          size_id: entry.sizeId,
          sku,
          min_stock_alert: 0,
          is_active: true,
        })
        .select('id');
      if (vErr) return { error: vErr.message };
      const newId = (v?.[0] as { id: string } | undefined)?.id;
      if (!newId) return { error: 'No se pudo crear la variante.' };

      const { error: invErr } = await supabase
        .from('inventory')
        .insert({
          variant_id: newId,
          available: entry.quantity,
          reserved: 0,
          consigned: 0,
        });
      if (invErr) return { error: invErr.message };
    }

    // Desactiva las variantes de tallas que ya no están seleccionadas.
    const selectedSizeIds = new Set(entries.map((e) => e.sizeId));
    for (const [sizeId, variantId] of variantBySize.entries()) {
      if (!selectedSizeIds.has(sizeId)) {
        await supabase
          .from('product_variants')
          .update({ is_active: false })
          .eq('id', variantId);
      }
    }

    return { error: null };
  } catch (err) {
    console.warn('[products] stock sync failed:', err);
    return { error: 'Error de conexión al sincronizar el stock.' };
  }
}